import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Typography,
    CircularProgress,
    Grid,
    Autocomplete,
} from '@mui/material';
import {useLazyGetAddressQuery} from '../api/services/addressService';
import {useGetCountriesQuery} from '../api/services/lookupsService';
import PropTypes from 'prop-types';

const POSTCODE_LOOKUP_COUNTRIES = ['GBR', 'IRL'];
const DISABLED_COUNTRIES = ['ENG', 'SCO', 'WAL'];

const firstNonEmpty = (...values) =>
    values.find(v => typeof v === 'string' && v.trim().length > 0) || '';

const formatAddressLabel = (option) =>
    [option?.line_1, option?.line_2].filter(v => typeof v === 'string' && v.trim()).join(', ');

const AdmicityAddressLookup = ({ onAddressSelect, initialValue, defaultCountry, readonly = false, errors, enableValidation = true }) => {
    const [postcode, setPostcode] = useState('');
    const [lastLookupPostcode, setLastLookupPostcode] = useState('');
    const [lookupFailureMessage, setLookupFailureMessage] = useState('');
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [addressFields, setAddressFields] = useState({
        apartment: '',
        houseName: '',
        houseNumber: '',
        street: '',
        town: '',
        county: '',
        district: '',
        postcode: '',
        country: ''
    });

    const isInitialMount = useRef(true);
    const prevAddressRef = useRef(null);

    const [trigger, { data: lookupData, error: lookupError, isFetching: isLookupFetching }] = useLazyGetAddressQuery();
    const { data: countries = [] } = useGetCountriesQuery();

    useEffect(() => {
        if (isInitialMount.current) {
            setSelectedCountry(initialValue?.countryCode || (readonly ? '' : defaultCountry));
        }
        if (initialValue && isInitialMount.current) {
            setAddressFields({
                apartment: initialValue.apartment || '',
                houseName: initialValue.houseName || '',
                houseNumber: initialValue.houseNumber || '',
                street: initialValue.street || '',
                town: initialValue.town || '',
                county: initialValue.county || '',
                district: initialValue.district || '',
                postcode: initialValue.postcode || '',
                country: initialValue.country || ''
            });
            isInitialMount.current = false;
        }
    }, [initialValue, defaultCountry, readonly]);

    useEffect(() => {
        if (!selectedAddress) return;

        const isIrish =
            selectedAddress.country === 'IRL' ||
            selectedAddress.country_iso === 'IRL' ||
            selectedAddress.country_iso_2 === 'IE';

        const simsDistrict = isIrish
            ? ''
            : firstNonEmpty(
                selectedAddress.double_dependant_locality,
                selectedAddress.dependant_locality
            );

        const newFields = {
            apartment: selectedAddress.sub_building_name || '',
            houseName: selectedAddress.building_name || '',
            houseNumber: selectedAddress.building_number || '',
            street: isIrish
                ? (selectedAddress.thoroughfare || selectedAddress.line_1 || '')
                : (selectedAddress.thoroughfare || ''),
            town: selectedAddress.post_town || '',
            county: isIrish
                ? firstNonEmpty(
                    (selectedAddress.native && selectedAddress.native.post_county) ? selectedAddress.native.post_county : '',
                    selectedAddress.county
                )
                : (selectedAddress.county || ''),
            district: simsDistrict,
            postcode: selectedAddress.postcode || '',
            country: selectedAddress.country || ''
        };

        setAddressFields(newFields);
    }, [selectedAddress]);

    useEffect(() => {
        if (readonly) return;
        const currentAddress = {
            ...addressFields,
            countryCode: selectedCountry
        };

        if (JSON.stringify(prevAddressRef.current) !== JSON.stringify(currentAddress)) {
            onAddressSelect(currentAddress);
            prevAddressRef.current = currentAddress;
        }
    }, [addressFields, selectedCountry, onAddressSelect]);

    useEffect(() => {
        if (
            lastLookupPostcode === '' ||
            postcode.trim() !== lastLookupPostcode ||
            isLookupFetching
        ) {
            return;
        }

        if (lookupError) {
            setLookupFailureMessage(lookupError.message || 'Failed to retrieve address.');
            return;
        }

        if (lookupData?.code != null && lookupData.code !== 2000) {
            setLookupFailureMessage(lookupData.message || 'Failed to retrieve address.');
            return;
        }

        setLookupFailureMessage('');
    }, [lookupData, lookupError, isLookupFetching, lastLookupPostcode, postcode]);

    const handleSearch = () => {
        if (postcode) {
            setLastLookupPostcode(postcode.trim());
            setLookupFailureMessage('');
            trigger(postcode);
        }
    };

    const resetAddressFields = (countryCode = selectedCountry) => {
        const cleared = {
            apartment: '',
            houseName: '',
            houseNumber: '',
            street: '',
            town: '',
            county: '',
            district: '',
            postcode: '',
            country: '',
        };
        setAddressFields(cleared);
        onAddressSelect({ ...cleared, countryCode });
    };

    const handlePostcodeChange = (event) => {
        setPostcode(event.target.value);
        setLastLookupPostcode('');
        setLookupFailureMessage('');
        setSelectedAddress(null);
        resetAddressFields();
    };

    const handleCountryChange = (e, newOption) => {
        if (readonly) return;
        const newCountry = newOption?.code || '';
        setSelectedCountry(newCountry);
        setSelectedAddress(null);
        setPostcode('');
        resetAddressFields(newCountry);
    };

    const handleAddressFieldChange = (field) => (event) => {
        if (readonly) return;
        setAddressFields((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleAddressSelect = (e, newValue) => {
        if (readonly) return;
        if (!newValue) {
            setSelectedAddress(null);
            resetAddressFields();
            return;
        }
        setSelectedAddress(newValue);
    };

    const requiredKeys = ['town', 'postcode'];
    const isIrish = selectedCountry === 'IRL';
    const isUK = selectedCountry === 'GBR';

    const inputsDisabled = !selectedCountry || isUK || readonly;

    const normalisedErrors =
        errors && typeof errors === 'object' && !Array.isArray(errors) ? errors : {};
    const parentHasErrors = Object.values(normalisedErrors).some(Boolean);

    const hasAllRequiredValues = requiredKeys.every((k) => {
        const v = addressFields[k];
        return v != null && String(v).trim() !== '';
    });

    const missing = requiredKeys.reduce((acc, k) => {
        const v = addressFields[k];
        if (v == null || String(v).trim() === '') acc[k] = 'This field is required';
        return acc;
    }, {});

    const showInlineErrors =
        enableValidation &&
        !readonly &&
        (isIrish || !POSTCODE_LOOKUP_COUNTRIES.includes(selectedCountry)) &&
        parentHasErrors;

    const showBlockError =
        enableValidation && !readonly && !isIrish && !isUK && parentHasErrors && !hasAllRequiredValues;

    const showLookupTopError =
        enableValidation &&
        !readonly &&
        POSTCODE_LOOKUP_COUNTRIES.includes(selectedCountry) &&
        parentHasErrors &&
        !hasAllRequiredValues;

    return (
        <div>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <Autocomplete
                        options={countries.filter(c => !DISABLED_COUNTRIES.includes(c.code))}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Country"
                                required={enableValidation}
                                disabled={readonly}
                                autoComplete="off"
                                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                            />
                        )}
                        disableClearable={enableValidation}
                        onChange={handleCountryChange}
                        value={countries.find(country => country.code === selectedCountry) || null}
                        fullWidth
                        disabled={readonly}
                    />
                </Grid>
            </Grid>

            {showBlockError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Please provide Street, Town, and Postcode. This field is required.
                </Typography>
            )}

            {POSTCODE_LOOKUP_COUNTRIES.includes(selectedCountry) && (
                <>
                    <Grid container spacing={2} alignItems="center" mt={2}>
                        <Grid item xs={8}>
                            <TextField
                                required={enableValidation && !isIrish}
                                label="Enter Postcode"
                                variant="outlined"
                                fullWidth
                                value={postcode}
                                onChange={handlePostcodeChange}
                                disabled={readonly}
                                autoComplete="off"
                                error={showLookupTopError}
                                helperText={
                                    showLookupTopError
                                        ? 'Please search and select an address, complete the fields below (Street, Town, Postcode).'
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearch}
                                disabled={!postcode || isLookupFetching || readonly}
                                fullWidth
                                sx={{
                                    backgroundColor: '#186BBE',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#155FA7',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {isLookupFetching ? <CircularProgress size={24} /> : 'Search'}
                            </Button>
                        </Grid>
                    </Grid>

                    {lookupFailureMessage && (
                        <Typography color="error" mt={2}>
                            {lookupFailureMessage}
                        </Typography>
                    )}

                    {lookupData && lookupData.code === 2000 && (
                        <div style={{ marginTop: '20px' }}>
                            <Autocomplete
                                disableClearable={false}
                                options={lookupData.result.filter((a) => a.country_iso === selectedCountry)}
                                getOptionLabel={(option) => formatAddressLabel(option)}
                                onChange={handleAddressSelect}
                                value={selectedAddress}
                                fullWidth
                                disabled={readonly}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Address"
                                        variant="outlined"
                                        disabled={readonly}
                                        autoComplete="off"
                                        inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                                    />
                                )}
                            />
                        </div>
                    )}
                </>
            )}

            <Grid container spacing={2} mt={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        label="Apartment"
                        value={addressFields.apartment}
                        onChange={handleAddressFieldChange('apartment')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        label="House Name"
                        value={addressFields.houseName}
                        onChange={handleAddressFieldChange('houseName')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        label="House Number"
                        value={addressFields.houseNumber}
                        onChange={handleAddressFieldChange('houseNumber')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        label="Street"
                        value={addressFields.street}
                        onChange={handleAddressFieldChange('street')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                        error={showInlineErrors && !!missing.street}
                        helperText={showInlineErrors ? missing.street : ''}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        label="District"
                        value={addressFields.district}
                        onChange={handleAddressFieldChange('district')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        required={enableValidation}
                        label="Town"
                        value={addressFields.town}
                        onChange={handleAddressFieldChange('town')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                        error={showInlineErrors && !!missing.town}
                        helperText={showInlineErrors ? missing.town : ''}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={inputsDisabled}
                        label="County"
                        value={addressFields.county}
                        onChange={handleAddressFieldChange('county')}
                        inputProps={{
                            maxLength: 40,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        disabled={!selectedCountry || isUK || readonly}
                        required={enableValidation}
                        label="Postcode"
                        value={addressFields.postcode}
                        onChange={handleAddressFieldChange('postcode')}
                        inputProps={{
                            maxLength: 8,
                            autoComplete: 'off',
                            style: {
                                color: 'black',
                                WebkitTextFillColor: 'black',
                            },
                        }}
                        autoComplete="off"
                        error={showInlineErrors && !!missing.postcode}
                        helperText={showInlineErrors ? missing.postcode : ''}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

AdmicityAddressLookup.defaultProps = {
    errors: {
        town: '',
        postcode: '',
        street: ''
    },
    enableValidation: true,
};

AdmicityAddressLookup.propTypes = {
    onAddressSelect: PropTypes.func.isRequired,
    initialValue: PropTypes.object,
    defaultCountry: PropTypes.string,
    readonly: PropTypes.bool,
    errors: PropTypes.object,
    enableValidation: PropTypes.bool,
};

export default AdmicityAddressLookup;